import { TResponseType } from '@@types/base';
import instance from '@apis/instance';
import currency from '@constants/currency';
import { AxiosRequestConfig } from 'axios';
import { IYogiyoStore } from 'interfaces/store';
import { signOut } from 'next-auth/react';

export const setComma = (value: string | number, minus = false): string => {
  if (!value || value === '0') return '0';
  if (typeof value === 'string' && value.length <= 3) return value;
  if (typeof value === 'number' && value < 1000) return '' + value;
  const result = new Intl.NumberFormat().format(+value);
  return minus ? '-' + result : result;
};

export const setFloorByMargin = (value: number, margin: number) =>
  Math.floor((value + value * (margin / 100)) / 100) * 100;

export const allowOnlyNumber = (value: string) => {
  return +value.replace(/[^0-9]/g, '');
};

export const allowOnlyNumberString = (value: string) => {
  return '' + value.replace(/[^0-9]/g, '');
};
export const debounce = (callback: (...props: any[]) => any, limit = 300) => {
  let timeout: ReturnType<typeof setTimeout>;
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(...args);
    }, limit);
  };
};

export const invalidateGetResponse = async <T>({
  url,
  options,
  errorMessage,
}: {
  url: string;
  options?: AxiosRequestConfig;
  errorMessage?: string;
}) => {
  try {
    const { data } = await instance.get<TResponseType<T>>(url, options);
    if (data.code === 404) {
      window.location.href = `${window.location.origin}/404`;
      throw new Error();
    } else if (data.code !== 200 || !data.data)
      throw errorMessage || data.message;
    else return data.data;
  } catch (e) {
    console.error(e);
    // window.location.href = window.location.origin + '/api/auth/signout';
    throw new Error('Failed fetch invalidate response');
  }
};

export const replaceDeliveryTime = (
  deliveryTime: string,
  replaceText = 'นาที',
) => {
  if (replaceText === '분') return deliveryTime;
  return deliveryTime.replace(/분/g, '');
};

export const replacePrice = (
  price: string,
  { won, free } = { won: 'วอน', free: 'Free' },
) => {
  return price.replace(/원/g, won).replace(/무료/g, free);
};

export const routeChangeCompleteHandler = () => {
  const scrollPosition = sessionStorage.getItem(
    `__scroll_${window.history.state.key}`,
  );
  if (scrollPosition) {
    const { y } = JSON.parse(scrollPosition);
    window.scrollTo({ top: y, left: 0 });
  }
};

export const setCurrency = (country: 'kr', price: string) => {
  return `${price}${currency[country]}`;
};

export const replaceDelivery = (delivery: string, margin: number) => {
  const price = delivery.match(/([0-9\,]+)/g);
  if (!price) return delivery;
  const tempText = delivery.replace(/[0-9\,]+/g, '{{}}');
  return price?.reduce((text, _price) => {
    return text.replace(
      '{{}}',
      '' +
        setCurrency(
          'kr',
          setComma(setFloorByMargin(allowOnlyNumber(_price), margin)),
        ),
    );
  }, tempText);
};

const toRadians = (degrees: number) => {
  return degrees * (Math.PI / 180);
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
) => {
  const R = 6371;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const a =
    sinLat * sinLat +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * sinLng * sinLng;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c;

  return distance;
};

export const calculateDegreeDifference = (
  baseCoords: { lat: number; lng: number },
  relativeCoords: { lat: number; lng: number },
) => {
  const latDiff = relativeCoords.lat - baseCoords.lat;
  const lngDiff = relativeCoords.lng - baseCoords.lng;
  const angle = Math.atan2(latDiff, lngDiff);
  const degreeDifference = (angle * (180 / Math.PI) + 360) % 360;

  return degreeDifference;
};

export const setPrice = ({
  promotion,
  isAddSection,
  finalPrice,
  originalPrice,
}: {
  promotion: number;
  isAddSection: boolean;
  finalPrice: number;
  originalPrice: number;
}) => {
  if (promotion === 0 && !isAddSection)
    return [{ originalPrice: finalPrice, finalPrice: finalPrice }];
  if (isAddSection) {
    if (promotion === 0) {
      return [
        {
          originalPrice: finalPrice,
          finalPrice: finalPrice,
          nextPrice: finalPrice - 1000,
        },
        {
          originalPrice: finalPrice,
          finalPrice: finalPrice - 1000,
        },
      ];
    } else {
      return [
        {
          originalPrice: originalPrice,
          finalPrice: originalPrice,
          nextPrice: originalPrice - finalPrice,
        },
        {
          originalPrice: originalPrice,
          finalPrice: finalPrice,
          nextPrice: 1000,
        },
        {
          originalPrice: finalPrice,
          finalPrice: finalPrice - 1000,
        },
      ];
    }
  } else {
    return [
      {
        originalPrice: originalPrice,
        finalPrice: originalPrice,
        nextPrice: originalPrice - finalPrice,
      },
      {
        originalPrice: originalPrice,
        finalPrice: finalPrice,
      },
    ];
  }
};

export const calculateMOA = ({
  moa,
  discounts,
}: {
  moa: number;
  discounts?: IYogiyoStore['discounts'];
}) => {
  if (!discounts || !discounts.additional || !discounts.additional.delivery)
    return moa;

  if (+discounts.additional.delivery.discount_mov > moa * 1.5) return moa;

  return Math.max(+moa, +discounts.additional.delivery.discount_mov);
};

export const viewOperatingHours = (hours: string) => {
  try {
    const splited = hours.split(':');
  
    if (splited.length < 3) return splited.join(':');
  
    return `${splited[0]}:${splited[1]}`;
  } catch (error) {
    return `00:00`;
  }
};

export const getAddressName = (address: string) => {
  if (!address) return '';
  const splited = address.split(' ');
  if (splited.length === 0) return '';

  const { length } = splited;

  return splited[length - 2];
};

export const addResFee = (value: string, addition?: number) => {
  if (!addition) return value;
  if (value.includes('~')) {
    const parts = value.split('~');

    const first = parseInt(parts[0].replace(/[^0-9]/g, ''), 10);
    const second = parseInt(parts[1].replace(/[^0-9]/g, ''), 10);

    return `${setComma(first + addition)}${currency.kr}~${setComma(
      second + addition,
    )}${currency.kr}`;
  } else {
    const price = parseInt(value.replace(/[^0-9]/g, ''));
    return `${setComma(price + addition)}${currency.kr}`;
  }
};
